import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './TrialBanner.css';
import { isAdminRole } from '../utils/planAccess';

const DISMISS_KEY = 'canira_trial_banner_dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const TrialBanner = () => {
  const { currentUser, getSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  // Check if the banner was previously dismissed (within the last 24 hours)
  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < DISMISS_DURATION_MS) {
        setDismissed(true);
      } else {
        localStorage.removeItem(DISMISS_KEY);
      }
    }
  }, []);

  // Under the site's fixed navigation the banner is fixed too (TrialBanner.css):
  // it sits at the navigation's real height and the page and sidebar start
  // below both. Both are measured, not assumed: the signed-in navigation is
  // ~44px (not the 72px the page padding assumes) and the banner wraps to
  // several lines on a phone.
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => {
      root.style.removeProperty('--trial-banner-h');
      root.style.removeProperty('--site-nav-h');
    };
    const el = document.querySelector('.navigation ~ .trial-banner');
    const nav = document.querySelector('.navigation');
    if (!el || !nav) {
      clear();
      return undefined;
    }
    const apply = () => {
      root.style.setProperty('--site-nav-h', `${nav.offsetHeight}px`);
      root.style.setProperty('--trial-banner-h', `${el.offsetHeight}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return clear;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    ro.observe(nav);
    return () => {
      ro.disconnect();
      clear();
    };
  });

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  // Pages with premium navigation - don't show trial banner on these
  const premiumPages = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/pricing',
    '/welcome',          // after checkout: the page itself says the trial started
    '/features',
    '/features/live-streaming',
    '/features/evergreen',
    '/features/registration',
    '/features/automation',
    '/dashboard/media',  // Media Library page - don't clutter with banners
    '/ai/thumbnail',     // AI Thumbnail Generator page
    '/ai/script'         // AI Script Generator page
  ];

  // Also hide on studio pages, AI tools, and embed pages
  // (A webinar's hub is a management page, not a viewer one: it keeps the
  // banner, so a lapsed host there can see why edits are refused.)
  if (location.pathname.startsWith('/studio/') ||
      (location.pathname.startsWith('/webinar/') && !location.pathname.endsWith('/hub')) ||
      location.pathname.startsWith('/embed/') ||
      location.pathname.startsWith('/ai/') ||
      location.pathname.startsWith('/ai-')) {
    return null;
  }

  // Hide on premium pages
  if (premiumPages.includes(location.pathname)) {
    return null;
  }

  if (!currentUser) return null;

  const status = getSubscriptionStatus();

  // Don't show banner for admins or paid subscribers
  if (isAdminRole(currentUser.role) || status.isActiveSubscription) {
    return null;
  }

  // When a lapsed host's automated webinars stop (or stopped) taking new
  // registrations — services/evergreenLapseService.js, via GET /me.
  const pauseSentence = (() => {
    if (!status.planLapse?.pauseAt) return null;
    if (status.planLapse.paused) {
      return ' Your automated webinars have stopped taking new registrations; people already registered still get their session.';
    }
    const when = new Date(status.planLapse.pauseAt).toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric',
    });
    return ` Your automated webinars stop taking new registrations on ${when}.`;
  })();

  // Plan ended: read-only. The routes a lapsed customer may still open
  // (contacts, a webinar's hub, the evergreen list) show this so a refused
  // action has its reason on screen — so it can't be dismissed, and a
  // dismissal of the trial banner doesn't hide it.
  if (!status.hasPlanAccess && status.lapsed) {
    return (
      <div className="trial-banner trial-expired" role="status">
        <div className="trial-banner-content">
          <div className="trial-banner-text">
            <strong>Your plan has ended, so you're in read-only mode.</strong>
            <span>
              {' '}Your webinars, registrants and recordings are safe, and you can view and export your
              contacts. Reactivate to create, edit, send or go live.
              {pauseSentence}
            </span>
          </div>
          <button className="trial-banner-cta" onClick={() => navigate('/pricing', { state: { lapsed: true } })}>
            Reactivate
          </button>
        </div>
      </div>
    );
  }

  // Don't show if dismissed within the last 24 hours
  if (dismissed) return null;

  // Close button shared across all banner variants
  const closeButton = (
    <button
      className="trial-banner-close"
      onClick={handleDismiss}
      aria-label="Dismiss banner"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );

  // Payment failed: still their plan while the card is retried, but it
  // won't be for long.
  if (status.hasPlanAccess && status.subscriptionStatus === 'past_due') {
    return (
      <div className="trial-banner trial-urgent" role="status">
        <div className="trial-banner-content">
          <div className="trial-banner-text">
            <strong>Your last payment didn't go through.</strong>
            <span> Update your card to keep your plan. We'll keep retrying for a few days.</span>
          </div>
          <button className="trial-banner-cta" onClick={() => navigate('/account')}>
            Update payment
          </button>
          {closeButton}
        </div>
      </div>
    );
  }

  // Still inside the product (the old client wall lets an expired trial in)
  // but the grace period has started for their automated webinars.
  if (pauseSentence) {
    return (
      <div className="trial-banner trial-urgent" role="status">
        <div className="trial-banner-content">
          <div className="trial-banner-text">
            <strong>Your plan has ended.</strong>
            <span>{pauseSentence} Reactivate to keep them running.</span>
          </div>
          <button className="trial-banner-cta" onClick={() => navigate('/pricing', { state: { lapsed: true } })}>
            Reactivate
          </button>
          {closeButton}
        </div>
      </div>
    );
  }

  // Trial expired banner \u2014 beta-mode framing. The premium tier won't
  // be self-serve until public launch, so the only path to keep
  // premium features is the Founding Member application at /beta.
  if (status.trialExpired) {
    return (
      <div className="trial-banner trial-expired">
        <div className="trial-banner-content">
          <div className="trial-banner-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="trial-banner-text">
            <strong>Your Serve trial has ended.</strong>
            <span> You’re now on Free — automated replays, multistreaming, unlimited broadcasts and 1080p are switched off. Upgrade to get them back at your founding rate, locked for life.</span>
          </div>
          <button className="trial-banner-cta" onClick={() => navigate('/pricing')}>
            Upgrade now
          </button>
          {closeButton}
        </div>
      </div>
    );
  }

  // Active trial / evaluation banner
  if (status.isTrialing) {
    const daysLeft = status.daysLeftInTrial;
    const isUrgent = daysLeft <= 3;

    return (
      <div className={`trial-banner ${isUrgent ? 'trial-urgent' : 'trial-active'}`}>
        <div className="trial-banner-content">
          <div className="trial-banner-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="trial-banner-text">
            <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</strong>
            <span> on your Serve trial. {isUrgent ? 'Upgrade to lock your founding rate before it ends!' : 'You\u2019re trying every Serve feature free \u2014 upgrade to keep them at your founding rate, locked for life.'}</span>
          </div>
          <button className="trial-banner-cta" onClick={() => navigate('/pricing')}>
            {isUrgent ? 'Upgrade Now' : 'Upgrade now'}
          </button>
          {closeButton}
        </div>
      </div>
    );
  }

  // No trial, no subscription (shouldn't happen but handle gracefully)
  if (!status.hasAccess) {
    return (
      <div className="trial-banner trial-expired">
        <div className="trial-banner-content">
          <div className="trial-banner-text">
            <strong>Upgrade to a paid plan to access premium features.</strong>
          </div>
          <button className="trial-banner-cta" onClick={() => navigate('/pricing')}>
            Upgrade now
          </button>
          {closeButton}
        </div>
      </div>
    );
  }

  return null;
};

export default TrialBanner;
